import { cva, type VariantProps } from 'class-variance-authority';

import { DARK_CONTROL } from '../../styles/elevation';

/**
 * Pagination visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`. Reference nodes:
 *   - 2348:15727 – Pagination standard, light (Beginning / Middle / End)
 *   - 3233:12804 – Pagination standard, dark
 *   - 2350:1020  – Pagination dot indicator, light (Primary / Secondary × 2–7)
 *   - 3233:12841 – Pagination dot indicator, dark
 *
 * The STANDARD variant's chevron buttons are instances of the shared Button
 * (`variant="secondary" size="m"` icon-only: same 40px square, glass
 * `bg-secondary-50/70` fill, `border-secondary-200` bevel, secondary-600
 * chevron) — Pagination.tsx composes `<Button>` directly, so only the center
 * page label and the dot indicator carry CVA classes here.
 *
 * Dark mode is automatic: every token auto-inverts via CSS vars (see
 * Button.variants.ts dark-mode note); no `dark:` classes needed. Verified
 * against the dark frames: page number secondary-900 → #f5f5f5, "/total"
 * secondary-500 → #737373, rest dot primary-200 → #5d0ec0, secondary rest
 * dot secondary-300 → #404040 — all exactly the auto-inverted shades.
 */

/**
 * Center "page / total" label chip of the standard pagination — Figma
 * "Button M" child of the Status Group Section: secondary-50 fill, 14px
 * radius, 16px horizontal padding, stretched to the 40px row height and
 * flexing to fill the space between the chevron buttons.
 */
export const paginationLabelVariants = cva(
  'h-10 min-w-0 flex-1 flex-row items-center justify-center gap-2 ' +
    `rounded-[14px] bg-secondary-50 px-4 ${DARK_CONTROL}`,
);

/**
 * Page-label text — Figma B1 (Mona Sans Medium 16). `tone` splits the
 * current page number (secondary-900) from the "/total" remainder
 * (secondary-500), per the two-span structure in the Figma node.
 */
export const paginationLabelTextVariants = cva(
  'whitespace-nowrap text-center text-b1 font-medium native:font-mona-medium',
  {
    variants: {
      tone: {
        current: 'text-secondary-900',
        total: 'text-fg-subtle',
      },
    },
    defaultVariants: { tone: 'current' },
  },
);

/**
 * Dot-indicator container — Figma "Pagination indicator": 8px padding, 4px
 * gap, 12.6px radius, 1px border. Primary = violet tint, secondary = gray.
 */
export const paginationDotsVariants = cva(
  // `inline-flex` hugs content on web (Badge precedent) instead of
  // stretching to the parent's width like the View primitive's block div.
  'inline-flex flex-row items-center justify-center gap-1 rounded-[12.6px] border p-2',
  {
    variants: {
      variant: {
        primary: 'bg-primary-50 border-primary-200',
        secondary: `bg-secondary-50 border-secondary-200 ${DARK_CONTROL}`,
      },
    },
    defaultVariants: { variant: 'primary' },
  },
);

/**
 * Single 8px dot — Figma "Pagination dot" (Active / Rest × Primary /
 * Secondary). Fills verified from the exported ellipse assets:
 *   primary   active #8e51ff (500) · rest #ddd6ff (200)
 *   secondary active #737373 (500) · rest #d4d4d4 (300)
 */
export const paginationDotVariants = cva('size-2 shrink-0 rounded-full', {
  variants: {
    variant: { primary: '', secondary: '' },
    active: { true: '', false: '' },
  },
  compoundVariants: [
    { variant: 'primary', active: true, class: 'bg-primary-500' },
    { variant: 'primary', active: false, class: 'bg-primary-200' },
    { variant: 'secondary', active: true, class: 'bg-secondary-500' },
    { variant: 'secondary', active: false, class: 'bg-secondary-300' },
  ],
  defaultVariants: { variant: 'primary', active: false },
});

export type PaginationDotsVariantProps = VariantProps<typeof paginationDotsVariants>;
